import { CommonModule } from '@angular/common';
import { Component, EventEmitter, HostListener, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

export type DatePickerFocus = 'day' | 'month' | 'year';

interface DatePickerCell {
  label: string;
  value: Date;
  inCurrentMonth?: boolean;
}

@Component({
  standalone: true,
  selector: 'app-date-picker',
  templateUrl: './date-picker.component.html',
  styleUrls: ['./date-picker.component.scss'],
  imports: [CommonModule, FormsModule, TranslateModule]
})
export class DatePickerComponent implements OnChanges {
  @Input() value: Date = new Date();
  @Output() valueChange = new EventEmitter<Date>();

  isOpen = false;
  focus: DatePickerFocus = 'day';
  draftDate = new Date();
  dayInput = '';
  monthInput = '';
  yearInput = '';

  readonly monthKeys = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
  readonly weekDayKeys = ['MON_SHORT', 'TUE_SHORT', 'WED_SHORT', 'THU_SHORT', 'FRI_SHORT', 'SAT_SHORT', 'SUN_SHORT'];

  constructor(private translateService: TranslateService) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['value']) {
      this.setDraftDate(this.value || new Date());
    }
  }

  toggle(event: Event): void {
    event.stopPropagation();
    this.isOpen = !this.isOpen;
    if (this.isOpen) {
      this.setDraftDate(this.value || new Date());
    }
  }

  setFocus(focus: DatePickerFocus, event?: Event): void {
    event?.stopPropagation();
    this.focus = focus;
  }

  previous(): void {
    if (this.focus === 'day') {
      this.draftDate = new Date(this.draftDate.getFullYear(), this.draftDate.getMonth() - 1, 1);
    } else if (this.focus === 'month') {
      this.draftDate = new Date(this.draftDate.getFullYear() - 1, this.draftDate.getMonth(), 1);
    } else {
      this.draftDate = new Date(this.draftDate.getFullYear() - 12, this.draftDate.getMonth(), 1);
    }
    this.syncInputs();
  }

  next(): void {
    if (this.focus === 'day') {
      this.draftDate = new Date(this.draftDate.getFullYear(), this.draftDate.getMonth() + 1, 1);
    } else if (this.focus === 'month') {
      this.draftDate = new Date(this.draftDate.getFullYear() + 1, this.draftDate.getMonth(), 1);
    } else {
      this.draftDate = new Date(this.draftDate.getFullYear() + 12, this.draftDate.getMonth(), 1);
    }
    this.syncInputs();
  }

  selectDay(cell: DatePickerCell): void {
    this.setDraftDate(cell.value);
  }

  selectMonth(monthIndex: number): void {
    this.setDraftDate(new Date(this.draftDate.getFullYear(), monthIndex, 1));
    this.focus = 'day';
  }

  selectYear(year: number): void {
    this.setDraftDate(new Date(year, this.draftDate.getMonth(), 1));
    this.focus = 'month';
  }

  confirm(): void {
    const day = this.clamp(Number(this.dayInput) || this.draftDate.getDate(), 1, this.daysInMonth(this.draftDate.getFullYear(), this.draftDate.getMonth()));
    const month = this.parseMonthInput();
    const year = Number(this.yearInput) || this.draftDate.getFullYear();
    const selected = new Date(year, month, day, 0, 0, 0, 0);
    this.setDraftDate(selected);
    this.valueChange.emit(selected);
    this.isOpen = false;
  }

  get dayCells(): DatePickerCell[] {
    const year = this.draftDate.getFullYear();
    const month = this.draftDate.getMonth();
    const first = new Date(year, month, 1);
    const mondayOffset = (first.getDay() + 6) % 7;
    const start = new Date(year, month, 1 - mondayOffset);
    const cells: DatePickerCell[] = [];

    for (let i = 0; i < 42; i++) {
      const date = new Date(start);
      date.setDate(start.getDate() + i);
      cells.push({
        label: String(date.getDate()),
        value: date,
        inCurrentMonth: date.getMonth() === month
      });
    }

    return cells;
  }

  get yearCells(): number[] {
    const start = Math.floor(this.draftDate.getFullYear() / 12) * 12;
    return Array.from({ length: 12 }, (_, index) => start + index);
  }

  get headerLabel(): string {
    if (this.focus === 'day') {
      return this.translateService.instant(`DATE_PICKER.MONTHS_FULL.${this.monthKeys[this.draftDate.getMonth()]}`);
    }
    if (this.focus === 'month') {
      return String(this.draftDate.getFullYear());
    }
    return `${this.yearCells[0]}-${this.yearCells[this.yearCells.length - 1]}`;
  }

  isSelectedDate(date: Date): boolean {
    return this.sameDate(date, this.draftDate);
  }

  isSelectedMonth(monthIndex: number): boolean {
    return this.draftDate.getMonth() === monthIndex;
  }

  isSelectedYear(year: number): boolean {
    return this.draftDate.getFullYear() === year;
  }

  get displayValue(): string {
    const month = this.translateService.instant(`DATE_PICKER.MONTHS_SHORT.${this.monthKeys[this.value.getMonth()]}`);
    return `${month} ${this.value.getDate()}, ${this.value.getFullYear()}`;
  }

  getMonthLabel(monthKey: string): string {
    return this.translateService.instant(`DATE_PICKER.MONTHS_SHORT.${monthKey}`);
  }

  getWeekDayLabel(weekDayKey: string): string {
    return this.translateService.instant(`DATE_PICKER.WEEKDAYS_NARROW.${weekDayKey}`);
  }

  @HostListener('document:click')
  close(): void {
    this.isOpen = false;
  }

  stopPropagation(event: Event): void {
    event.stopPropagation();
  }

  private setDraftDate(date: Date): void {
    this.draftDate = new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0);
    this.syncInputs();
  }

  private syncInputs(): void {
    this.dayInput = String(this.draftDate.getDate()).padStart(2, '0');
    this.monthInput = this.getMonthLabel(this.monthKeys[this.draftDate.getMonth()]);
    this.yearInput = String(this.draftDate.getFullYear());
  }

  private parseMonthInput(): number {
    const normalized = this.monthInput.trim().toLowerCase();
    const monthIndex = this.monthKeys.findIndex(monthKey => this.getMonthLabel(monthKey).toLowerCase() === normalized);
    if (monthIndex >= 0) {
      return monthIndex;
    }
    const numericMonth = Number(this.monthInput);
    return this.clamp(numericMonth || this.draftDate.getMonth() + 1, 1, 12) - 1;
  }

  private daysInMonth(year: number, month: number): number {
    return new Date(year, month + 1, 0).getDate();
  }

  private clamp(value: number, min: number, max: number): number {
    return Math.min(Math.max(value, min), max);
  }

  private sameDate(first: Date, second: Date): boolean {
    return first.getFullYear() === second.getFullYear() &&
      first.getMonth() === second.getMonth() &&
      first.getDate() === second.getDate();
  }
}
